<template>
  <div :class="['page', $gameTemplateStyle.joinGamePage]">
    <div :class="[$globalStyle.headerContainer]">
      <div :class="[$globalStyle.header, $globalStyle.playerHeader]">
        <div :class="[$globalStyle.headerLeft]">
          <div :class="$globalStyle.logoContainer">
            <img :src="saboteurLogo" alt="icon" :class="$globalStyle.headerLogo" />
            <div :class="$globalStyle.headerTitle">{{ $t('saboteur') }}</div>
          </div>
        </div>
      </div>

      <HeaderActionButton
        :text="$t('reset')"
        :iconLink="resetIcon"
        :clickFunction="resetForm"
        backBg="#000000"
        frontBg="#676767"
        textColor="#FFFFFF"
        :rightIcon="true"
        :icon-only="isMobile"
        :button-width="isMobile ? '60px' : isTablet ? '130px' : '160px'"
      />
    </div>

    <div :class="$gameTemplateStyle.bodyContainer">
      <h1 :class="$gameTemplateStyle.bodyText">{{ $t('join-a-game') }}</h1>

      <div :class="$gameTemplateStyle.formContainer">
        <div :class="$gameTemplateStyle.form">
          <UtilInput
            :label="$t('player-name')"
            :iconLink="detectiveHatIcon"
            :placeholder="$t('enter-name')"
            :value="form.playerName"
            @update:value="form.playerName = $event"
          />

          <UtilInput
            :label="$t('code')"
            inputType="number"
            :iconLink="passwordKeyIcon"
            :placeholder="$t('enter-code')"
            :value="form.joinCode"
            @update:value="form.joinCode = $event"
          />
        </div>
      </div>

      <div :class="$gameTemplateStyle.ctaContainer">
        <UtilButton
          :class="$gameTemplateStyle.cta"
          :text="$t('join-game')"
          :clickFunction="joinGame"
          frontBg="#DB7532"
          backBg="#A14000"
          textColor="#FFFFFF"
          :button-width="isMobile ? '200px' : isTablet ? '220px' : '240px'"
        />
      </div>
    </div>

    <div v-if="isLoading" class="loader-container">
      <img :src="playerLoaderGif" alt="loader-gif" class="loader-gif" />
    </div>
    <div v-if="isReconnectAttempt" class="reloader-container">
      <img :src="appLoaderGif " alt="loader-gif" class="loader-gif" />
      <div :class="$style.reconnectLoaderText">
        Reconnecting...
      </div>
    </div>
  </div>
</template>

<script>
import styles from "@/css/CreateGameView.module.css";
import gameTemplateStyles from "@/css/GameViewTemplate.module.css";
import globalStyles from "@/css/global.module.css";
import UtilButton from "@/components/util/UtilButton.vue";
import SaboteurLogo from "../assets/saboteur-logo.svg";
import ResetIcon from "@/assets/icons/reset.svg";
import UtilInput from "@/components/util/UtilInput.vue";
import DetectiveHatIcon from "@/assets/icons/detective-hat.svg";
import PasswordKeyIcon from "@/assets/icons/password-key.svg";
import { toast } from "vue3-toastify";
import gameService from "@/services/game.service";
import PlayerLoaderGif from "@/assets/gifs/player-loader.svg";
import HostLoaderGif from "@/assets/gifs/host-loader.svg";
import { handleApiResponseError } from "@/utils/url";
import { useGameStore } from "@/stores/gameStore";
import { getFilteredText } from "@/utils/functions";
import { textLabels } from "@/utils/data";
import HeaderActionButton from "@/components/buttons/HeaderActionButton.vue";
import useDeviceType from "@/utils/useDeviceType";
import AppLoaderGif from "@/assets/gifs/app-loader.svg";

const device = useDeviceType();

export default {
  name: "JoinGameView",
  components: { UtilButton, UtilInput, HeaderActionButton },

  data() {
    return {
      isReconnectAttempt: false,
      isReconnect: false,
      isHostReconnect: false,
      saboteurLogo: SaboteurLogo,
      resetIcon: ResetIcon,
      detectiveHatIcon: DetectiveHatIcon,
      passwordKeyIcon: PasswordKeyIcon,
      playerLoaderGif: PlayerLoaderGif,
      appLoaderGif: AppLoaderGif,
      hostLoaderGif: HostLoaderGif,
      isLoading: false,

      form: {
        playerName: "",
        joinCode: "",
      },
    };
  },

  computed: {
    $style() {
      return styles;
    },
    $gameTemplateStyle() {
      return gameTemplateStyles;
    },
    $globalStyle() {
      return globalStyles;
    },
    isMobile() {
      return device.isMobile.value;
    },
    isTablet() {
      return device.isTablet.value;
    },
  },

  mounted() {
    // ✅ Only restore player name (NOT join code)
    const playerName = localStorage.getItem("lastPlayerName");
    if (playerName) {
      this.form.playerName = playerName;
    }
  },

  methods: {
    resetForm() {
      this.form.playerName = "";
      this.form.joinCode = "";

      const lastJoinCode = localStorage.getItem("lastJoinCode");

      if (lastJoinCode) {
        localStorage.removeItem(`rejoinToken_${lastJoinCode}`);
      }

      localStorage.removeItem("lastJoinCode");
      localStorage.removeItem("lastPlayerName");
    },

    validateJoinGameForm() {
      if (!this.form.playerName) {
        toast(this.$t("error-player-name"), {
          type: "error",
        });

        return false;
      }

      if (this.form.playerName.length > 16) {
        toast(this.$t("error-player-length"), {
          type: "error",
        });

        return false;
      }

      if (!this.form.joinCode) {
        toast(this.$t("error-join-code"), {
          type: "error",
        });

        return false;
      }

      return true;
    },

    handleJoinSuccess(gameData) {
      // ✅ Store fresh session
      localStorage.setItem("lastJoinCode", gameData.joinCode);
      localStorage.setItem("lastPlayerName", this.form.playerName);

      if (gameData.rejoinToken) {
        localStorage.setItem(
          `rejoinToken_${gameData.joinCode}`,
          JSON.stringify({
            token: gameData.rejoinToken,
            createdAt: Date.now(),
          })
        );
      }
      const modifiedTexts = gameData.texts.map((text, index) => ({
        id: gameData.uniqueTextId[index],
        label: textLabels[index + 1],
        content: text.content,
      }));

      const gameStore = useGameStore();

      const isHostUser =
        Number(gameData.hostId) === Number(gameData.uniquePlayerId);
      const resolvedUserName = isHostUser
      ? gameData.hostUsername
      : gameData.players.find(
          (p) => Number(p.id) === Number(gameData.uniquePlayerId)
        )?.username || this.form.playerName;
      gameStore.setGameData(
        {
          host: {
            id: parseInt(gameData.hostId),
            name: gameData.hostUsername,
          },
          players: gameData.players ?? [],
          joinCode: gameData.joinCode,
          allowedPlayers: gameData.playerNum,
          groupLimit: gameData.groupLimit,
          readingTime: gameData.readingTime,
          discussionTime: gameData.discussionTime,
          textTitle: gameData.textTitle,
          discipline: gameData.discipline,
          originalText: gameData.originalText
            ? getFilteredText(gameData.originalText)
            : "",
          combinedText: gameData.originalText,
          texts: modifiedTexts,
          fbGameDocId: gameData.fbGameDocId,
          gameId: gameData.gameId,
          phase: gameData.phase || "JOIN",
          enablePreliminaryVoteReason: gameData.enablePreliminaryVoteReason,
          enableFinalReason: Number(gameData.enableVoteReason),
        },
        isHostUser,
        {
          id: gameData.uniquePlayerId,
          name: resolvedUserName,
          avatar: gameData.userAvatarUrl,
        },
        false
      );
      this.isReconnect = gameData.isReconnect === true;
      this.isHostReconnect = gameData.isHostReconnect === true;

      const isReconnecting = this.isReconnect || this.isHostReconnect;

      sessionStorage.setItem("isReconnectFlow", isReconnecting ? "true" : "false");
      this.$router.push({name: "play-game"});
    },

    async joinGame() {
      if (this.isLoading) return;
      try {
        if (!this.validateJoinGameForm()) return;
        // ------------------------------------------------
        // RECONNECT TOKEN HANDLING
        // ------------------------------------------------
        const storedReconnect = localStorage.getItem(
          `rejoinToken_${this.form.joinCode}`
        );
        let existingToken = null;
        if (storedReconnect) {
          try {
            const parsed = JSON.parse(storedReconnect);
            const isExpired =
              Date.now() - parsed.createdAt >
              2 * 24 * 60 * 60 * 1000;
            // send modified token so backend knows
            existingToken = isExpired
              ? `${parsed.token}_expired`
              : parsed.token;

          } catch (e) {
            localStorage.removeItem(
              `rejoinToken_${this.form.joinCode}`
            );
            toast(
              "Reconnect session invalid. Please join again.",
              { type: "warning" }
            );
            return;
          }
        }
        this.isReconnectAttempt = !!existingToken;
        this.isLoading = !this.isReconnectAttempt;
        const res = await gameService.playerJoinGame({
          playerName: this.form.playerName,
          joinCode: String(this.form.joinCode),
          rejoinToken: existingToken || null,
        });
        if (res?.status === 200 && res.data) {
          this.handleJoinSuccess(res.data);
        } else {
          toast(this.$t("unexpected-error"), {
            type: "error",
          });
        }
      } catch (error) {
        const detail = error?.response?.data?.error?.message;
        if (
          detail === "Game already completed" ||
          detail === "Game already ended"
        ) {
          this.isReconnectAttempt = false;
          toast(detail, { type: "error" });
          localStorage.removeItem(
            `rejoinToken_${this.form.joinCode}`
          );
          this.$router.push({ name: "join-game" });
          return;
        }
        if (detail === "Reconnect session expired") {
          this.isReconnectAttempt = false;

          localStorage.removeItem(
            `rejoinToken_${this.form.joinCode}`
          );
          toast(
            "Your reconnect session expired. Please join again.",
            { type: "warning" }
          );
          return;
        }
        if (detail === "Username already taken") {
          this.isReconnectAttempt = false;
          toast(detail, { type: "error" });
          return;
        }
        if (detail === "Game not found") {
          this.isReconnectAttempt = false;
          toast("Invalid join code", {
            type: "error",
          });
          return;
        }
        handleApiResponseError(error);
      } finally {
        this.isLoading = false;
      }
    },
  }
};
</script>